import React from 'react';
import { ContentType } from 'types/mods';
import { ModifierFieldProps } from './types';
import SearchableSelect from '../../UI/SearchableSelect';

const ModifierField: React.FC<ModifierFieldProps> = ({
  modifiers,
  setModifiers,
  availableMods,
  label,
  contentTypes,
  setContentType,
  maxCount = 2
}) => {
  // Add a new modifier
  const addModifier = () => {
    if (modifiers.length < maxCount) {
      setModifiers([...modifiers, { text: '', value: '' }]);
    }
  };

  // Remove a modifier
  const removeModifier = (index: number) => {
    const newModifiers = [...modifiers];
    newModifiers.splice(index, 1);
    setModifiers(newModifiers);
  };

  // Clear a modifier (reset to empty values without removing it)
  const clearModifier = (index: number) => {
    const newModifiers = [...modifiers];
    newModifiers[index] = { text: '', value: '' };
    setModifiers(newModifiers);
  };

  // Update a modifier text
  const updateModifierText = (index: number, text: string) => {
    // Check if this modifier is already selected in another slot
    const isDuplicate = modifiers.some((mod, i) => i !== index && mod.text === text && text !== '');
    
    if (isDuplicate) {
      // Show alert if duplicate
      alert(`${text} is already selected. Please choose a different ${label.slice(0, -1).toLowerCase()}.`);
      return;
    }
    
    const newModifiers = [...modifiers];
    newModifiers[index].text = text;
    
    // Set default value within range if available
    const selectedMod = availableMods.find(mod => mod.description === text);
    if (selectedMod) {
      if (selectedMod.range) {
        const min = selectedMod.range[0];
        const max = selectedMod.range[1];
        // Calculate middle value with proper decimal handling
        const hasDecimals = (min % 1 !== 0) || (max % 1 !== 0);
        const middleValue = (min + max) / 2;
        // If the range has decimal values, keep the decimal precision
        // Otherwise, round to the nearest integer
        const defaultValue = hasDecimals ? middleValue : Math.ceil(middleValue);
        newModifiers[index].value = defaultValue.toString();
      } else {
        // For modifiers without a range, set a default value of "1"
        // or any other appropriate default value for your application
        newModifiers[index].value = "1";
      }
    }
    
    setModifiers(newModifiers);
  };

  // Update a modifier value
  const updateModifierValue = (index: number, value: string) => {
    const newModifiers = [...modifiers];
    newModifiers[index].value = value;
    setModifiers(newModifiers);
  };

  // Get min value for a modifier
  const getMinValue = (text: string) => {
    const selectedMod = availableMods.find(mod => mod.description === text);
    return selectedMod?.range?.[0] ?? 0;
  };

  // Get max value for a modifier
  const getMaxValue = (text: string) => {
    const selectedMod = availableMods.find(mod => mod.description === text);
    return selectedMod?.range?.[1] ?? 100;
  };

  // Get step value for a modifier
  const getStepValue = (text: string) => {
    const selectedMod = availableMods.find(mod => mod.description === text);
    return selectedMod?.range?.[0] && selectedMod.range[0] % 1 !== 0 ? 0.1 : 1;
  };


  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium">{label}</label>
        <button
          type="button"
          onClick={addModifier}
          disabled={modifiers.length >= maxCount}
          className={`text-xs px-2 py-1 rounded ${
            modifiers.length >= maxCount 
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
              : 'bg-blue-700 hover:bg-blue-600 text-white'
          }`}
        >
          Add {label.slice(0, -2)} {/* Remove 's' from the end of the label */}
        </button>
      </div>
      
      {modifiers.map((modifier, index) => (
        <div key={`${label.toLowerCase()}-${index}`} className="mb-3 bg-gray-700 p-3 rounded">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-gray-300">{label.slice(0, -1)} {index + 1}</span>
            <div>
              {/* Show clear button only when there's content in the modifier */}
              {modifier.text && (
                <button
                  type="button"
                  onClick={() => clearModifier(index)}
                  className="text-yellow-400 hover:text-yellow-300 text-xs mr-2"
                >
                  Clear
                </button>
              )}
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeModifier(index)}
                  className="text-red-400 hover:text-red-300 text-xs"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <div className="flex gap-2 mb-2">
            <select 
              value={contentTypes[index] || ''} 
              onChange={(e) => setContentType(index, e.target.value as ContentType)}
              className="w-1/3 p-2 rounded bg-gray-800 text-white text-sm"
            >
              <option value="">All Content</option>
              {Object.values(ContentType).map((type) => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
            
            <SearchableSelect
              value={modifier.text}
              onChange={(value) => updateModifierText(index, value)}
              options={availableMods
                .filter(mod => !contentTypes[index] || mod.contentType === contentTypes[index])
                // Only show mods not selected in other slots
                .filter(mod => mod.description === modifier.text || 
                               !modifiers.some((m, i) => i !== index && m.text === mod.description))
                .map(mod => ({
                  value: mod.description,
                  label: mod.description
                }))}
              placeholder={`Select ${label.slice(0, -2)}`}
              className="w-2/3"
            />
          </div>
          <div>
            <input 
              type="number" 
              value={modifier.value} 
              onChange={(e) => updateModifierValue(index, e.target.value)} 
              className="w-full p-2 rounded bg-gray-800 text-white text-sm"
              placeholder="Enter value..."
              min={getMinValue(modifier.text)}
              max={getMaxValue(modifier.text)}
              step={getStepValue(modifier.text)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ModifierField;